import React, { useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { db } from "@/lib/firebase";
import { collection, addDoc, onSnapshot, query, where, orderBy, deleteDoc, doc, updateDoc, Timestamp } from "firebase/firestore";
import { FileText, Plus, Trash2, Edit, Eye, Search, ChevronLeft, ChevronRight } from "lucide-react";
import { 
  Pagination, 
  PaginationContent, 
  PaginationItem, 
  PaginationLink, 
  PaginationNext, 
  PaginationPrevious 
} from "@/components/ui/pagination";
import MessageDialog from "@/components/ui/MessageDialog";

interface ORBatch {
  id: string;
  branch: string;
  prefix: string;
  rangeStart: number;
  rangeEnd: number;
  generatedNumbers: string[];
  usedNumbers: string[];
  availableNumbers: string[];
  createdAt: any;
  createdBy: string;
  active: boolean; // new field
}

interface Branch {
  id: string;
  name: string;
}

const ORManagement: React.FC = () => {
  const [branches, setBranches] = useState<Branch[]>([]);
  const [orBatches, setOrBatches] = useState<ORBatch[]>([]);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showViewDialog, setShowViewDialog] = useState(false);
  const [selectedBatch, setSelectedBatch] = useState<ORBatch | null>(null);
  const [loading, setLoading] = useState(false);
  
  // Filter and search states
  const [selectedBranchFilter, setSelectedBranchFilter] = useState<string>("");
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [activeFilter, setActiveFilter] = useState<'active' | 'inactive'>('active');
  
  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 6;
  
  const [createData, setCreateData] = useState({
    branch: "",
    prefix: "",
    rangeStart: "",
    rangeEnd: ""
  });

  const [message, setMessage] = useState({
    open: false,
    text: "",
    type: "info" as "info" | "success" | "error"
  });

  useEffect(() => {
    // Listen to branches
    const branchesUnsubscribe = onSnapshot(collection(db, "branches"), (snapshot) => {
      setBranches(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as Branch)));
    });

    // Listen to OR batches
    const orBatchesUnsubscribe = onSnapshot(
      query(collection(db, "or"), orderBy("createdAt", "desc")),
      (snapshot) => {
        setOrBatches(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as ORBatch)));
      }
    );

    return () => {
      branchesUnsubscribe();
      orBatchesUnsubscribe();
    };
  }, []);

  // Set initial branch filter to first available branch
  useEffect(() => {
    if (branches.length > 0 && !selectedBranchFilter) {
      setSelectedBranchFilter(branches[0].id);
    }
  }, [branches, selectedBranchFilter]);

  const generateORNumbers = (prefix: string, start: number, end: number): string[] => {
    const numbers: string[] = [];
    // Check if we're using prefix + range (old method) or direct number range (new method)
    if (prefix && start < 1000) {
      // Old method: prefix + sequential range (e.g., prefix "10000" + range 1-50 = 100001 to 100050)
      for (let i = start; i <= end; i++) {
        numbers.push(`${prefix}${i.toString().padStart(2, '0')}`);
      }
    } else {
      // New method: direct number range (e.g., 1000051 to 10000100)
      for (let i = start; i <= end; i++) {
        numbers.push(i.toString());
      }
    }
    return numbers;
  };

  const handleCreateBatch = async () => {
    if (!createData.branch || !createData.rangeStart || !createData.rangeEnd) {
      setMessage({
        open: true,
        text: "Please fill in branch and range fields",
        type: "error"
      });
      return;
    }

    const start = parseInt(createData.rangeStart);
    const end = parseInt(createData.rangeEnd);

    if (isNaN(start) || isNaN(end) || start > end) {
      setMessage({
        open: true,
        text: "Invalid range. Start must be less than or equal to end",
        type: "error"
      });
      return;
    }

    if (end - start + 1 > 1000) {
      setMessage({
        open: true,
        text: "Range too large. Maximum 1000 numbers per batch",
        type: "error"
      });
      return;
    }

    setLoading(true);
    try {
      const generatedNumbers = generateORNumbers(createData.prefix, start, end);
      
      await addDoc(collection(db, "or"), {
        branch: createData.branch,
        prefix: createData.prefix,
        rangeStart: start,
        rangeEnd: end,
        generatedNumbers,
        usedNumbers: [],
        availableNumbers: generatedNumbers,
        createdAt: Timestamp.now(),
        createdBy: "admin" // You can get this from auth context
      });

      setMessage({
        open: true,
        text: `Successfully created ${generatedNumbers.length} OR numbers`,
        type: "success"
      });

      setShowCreateDialog(false);
      setCreateData({
        branch: "",
        prefix: "",
        rangeStart: "",
        rangeEnd: ""
      });
    } catch (error) {
      console.error("Error creating OR batch:", error);
      setMessage({
        open: true,
        text: "Failed to create OR batch",
        type: "error"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteBatch = async (batchId: string) => {
    if (window.confirm("Are you sure you want to delete this OR batch?")) {
      try {
        await deleteDoc(doc(db, "or", batchId));
        setMessage({
          open: true,
          text: "OR batch deleted successfully",
          type: "success"
        });
      } catch (error) {
        console.error("Error deleting OR batch:", error);
        setMessage({
          open: true,
          text: "Failed to delete OR batch",
          type: "error"
        });
      }
    }
  };

  const markBatchActiveStatus = async (batch: ORBatch) => {
    const isActive = batch.availableNumbers.length > 0;
    if (batch.active !== isActive) {
      await updateDoc(doc(db, 'or', batch.id), { active: isActive });
    }
  };

  useEffect(() => {
    orBatches.forEach(batch => {
      markBatchActiveStatus(batch);
    });
  }, [orBatches]);

  const getBranchName = (branchId: string) => {
    const branch = branches.find(b => b.id === branchId);
    return branch?.name || branchId;
  };

  const getUsageStats = (batch: ORBatch) => {
    const total = batch.generatedNumbers.length;
    const used = batch.usedNumbers.length;
    const available = total - used;
    const percentage = total > 0 ? Math.round((used / total) * 100) : 0;
    
    return { total, used, available, percentage };
  };

  // Filter and search logic
  const getFilteredBatches = () => {
    let filtered = orBatches;

    // Filter by branch
    if (selectedBranchFilter) {
      filtered = filtered.filter(batch => batch.branch === selectedBranchFilter);
    }

    // Filter by active/inactive only
    filtered = filtered.filter(batch => batch.active === (activeFilter === 'active'));

    // Filter by search term (searches in OR range, prefix, and branch name)
    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(batch => {
        const branchName = getBranchName(batch.branch).toLowerCase();
        const prefix = batch.prefix?.toLowerCase() || "";
        const range = batch.prefix ? 
          `${batch.prefix}${batch.rangeStart.toString().padStart(2, '0')}-${batch.prefix}${batch.rangeEnd.toString().padStart(2, '0')}` :
          `${batch.rangeStart}-${batch.rangeEnd}`;
        
        return branchName.includes(term) || 
               prefix.includes(term) || 
               range.toLowerCase().includes(term) ||
               batch.rangeStart.toString().includes(term) ||
               batch.rangeEnd.toString().includes(term);
      });
    }

    return filtered;
  };

  const filteredBatches = getFilteredBatches();

  // Pagination logic
  const totalPages = Math.ceil(filteredBatches.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedBatches = filteredBatches.slice(startIndex, endIndex);

  // Reset current page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [selectedBranchFilter, searchTerm, activeFilter]);

  const goToPage = (page: number) => {
    setCurrentPage(page);
  };

  const goToPrevPage = () => {
    setCurrentPage(prev => Math.max(1, prev - 1));
  };

  const goToNextPage = () => {
    setCurrentPage(prev => Math.min(totalPages, prev + 1));
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-3xl font-bold tracking-tight">OR Management</h2>
          <p className="text-muted-foreground">
            Manage Official Receipt numbers for all branches
          </p>
        </div>
        <Button onClick={() => setShowCreateDialog(true)}>
          <Plus className="h-4 w-4 mr-2" />
          Create OR Batch
        </Button>
      </div>

      {/* Filters and Search */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col sm:flex-row gap-4 items-center">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
                <Input
                  placeholder="Search by branch, prefix, or OR range..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Label htmlFor="branchFilter" className="whitespace-nowrap">Filter by Branch:</Label>
              <Select
                value={selectedBranchFilter}
                onValueChange={setSelectedBranchFilter}
              >
                <SelectTrigger className="w-[200px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {branches.map(branch => (
                    <SelectItem key={branch.id} value={branch.id}>
                      {branch.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-2">
              <Label htmlFor="activeFilter" className="whitespace-nowrap">Show:</Label>
              <Select value={activeFilter} onValueChange={value => setActiveFilter(value as 'active' | 'inactive')}>
                <SelectTrigger className="w-[120px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="inactive">Inactive</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {searchTerm.trim() && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  setSearchTerm("");
                }}
              >
                Clear Search
              </Button>
            )}
          </div>
          <div className="mt-4 text-sm text-muted-foreground">
            Showing {startIndex + 1}-{Math.min(endIndex, filteredBatches.length)} of {filteredBatches.length} OR batches
            {orBatches.length !== filteredBatches.length && ` (${orBatches.length} total)`}
          </div>
        </CardContent>
      </Card>

      {/* OR Batches Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {paginatedBatches.map((batch) => {
          const stats = getUsageStats(batch);
          return (
            <Card key={batch.id} className="relative">
              <CardHeader className="pb-3">
                <div className="flex justify-between items-start">
                  <div>
                    <CardTitle className="text-lg">{getBranchName(batch.branch)}</CardTitle>
                    <p className="text-sm text-muted-foreground">
                      {batch.prefix ? 
                        `${batch.prefix}${batch.rangeStart.toString().padStart(2, '0')} - ${batch.prefix}${batch.rangeEnd.toString().padStart(2, '0')}` :
                        `${batch.rangeStart} - ${batch.rangeEnd}`
                      }
                    </p>
                  </div>
                  <Badge variant={stats.available > 0 ? "default" : "destructive"}>
                    {stats.available > 0 ? "Active" : "Exhausted"}
                  </Badge>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span>Total Numbers:</span>
                    <span className="font-medium">{stats.total}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span>Used:</span>
                    <span className="font-medium text-red-600">{stats.used}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span>Available:</span>
                    <span className="font-medium text-green-600">{stats.available}</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div 
                      className="bg-blue-600 h-2 rounded-full transition-all duration-300" 
                      style={{ width: `${stats.percentage}%` }}
                    />
                  </div>
                  <p className="text-xs text-center text-muted-foreground">
                    {stats.percentage}% used
                  </p>
                </div>

                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setSelectedBatch(batch);
                      setShowViewDialog(true);
                    }}
                    className="flex-1"
                  >
                    <Eye className="h-4 w-4 mr-1" />
                    View
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleDeleteBatch(batch.id)}
                    className="text-red-600 hover:text-red-700"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Pagination Controls */}
      {filteredBatches.length > 0 && totalPages > 1 && (
        <div className="flex justify-center items-center space-x-4 mt-6">
          <div className="flex items-center space-x-2">
            <Button
              variant="outline"
              size="sm"
              onClick={goToPrevPage}
              disabled={currentPage === 1}
            >
              <ChevronLeft className="h-4 w-4" />
              Previous
            </Button>
            
            <div className="flex items-center space-x-1">
              {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                <Button
                  key={page}
                  variant={currentPage === page ? "default" : "outline"}
                  size="sm"
                  onClick={() => goToPage(page)}
                  className="min-w-[40px]"
                >
                  {page}
                </Button>
              ))}
            </div>

            <Button
              variant="outline"
              size="sm"
              onClick={goToNextPage}
              disabled={currentPage === totalPages}
            >
              Next
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
          
          <div className="text-sm text-muted-foreground">
            Page {currentPage} of {totalPages}
          </div>
        </div>
      )}

            {paginatedBatches.length === 0 && orBatches.length === 0 && (
        <Card className="text-center py-12">
          <CardContent>
            <FileText className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
            <h3 className="text-lg font-semibold mb-2">No OR batches created yet</h3>
            <p className="text-muted-foreground mb-4">
              Create your first OR batch to get started
            </p>
            <Button onClick={() => setShowCreateDialog(true)}>
              <Plus className="h-4 w-4 mr-2" />
              Create OR Batch
            </Button>
          </CardContent>
        </Card>
      )}

      {paginatedBatches.length === 0 && orBatches.length > 0 && (
        <Card className="text-center py-12">
          <CardContent>
            <Search className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
            <h3 className="text-lg font-semibold mb-2">No OR batches found</h3>
            <p className="text-muted-foreground mb-4">
              Try adjusting your search criteria or switch to a different branch
            </p>
            <Button
              variant="outline"
              onClick={() => {
                setSearchTerm("");
              }}
            >
              Clear Search
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Create OR Batch Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create OR Batch</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="branch">Branch</Label>
              <Select
                value={createData.branch}
                onValueChange={(value) => setCreateData({ ...createData, branch: value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select branch" />
                </SelectTrigger>
                <SelectContent>
                  {branches.map(branch => (
                    <SelectItem key={branch.id} value={branch.id}>
                      {branch.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="prefix">Prefix (Optional - for old format)</Label>
              <Input
                id="prefix"
                placeholder="e.g., 10000 (leave empty for direct number range)"
                value={createData.prefix}
                onChange={(e) => setCreateData({ ...createData, prefix: e.target.value })}
              />
              <p className="text-xs text-muted-foreground mt-1">
                Use prefix for format like 100001-100050. Leave empty for direct ranges like 1000051-10000100.
              </p>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="rangeStart">Start Number</Label>
                <Input
                  id="rangeStart"
                  type="number"
                  placeholder={createData.prefix ? "1" : "1000051"}
                  value={createData.rangeStart}
                  onChange={(e) => setCreateData({ ...createData, rangeStart: e.target.value })}
                />
              </div>
              <div>
                <Label htmlFor="rangeEnd">End Number</Label>
                <Input
                  id="rangeEnd"
                  type="number"
                  placeholder={createData.prefix ? "50" : "10000100"}
                  value={createData.rangeEnd}
                  onChange={(e) => setCreateData({ ...createData, rangeEnd: e.target.value })}
                />
              </div>
            </div>

            {createData.rangeStart && createData.rangeEnd && (
              <div className="p-3 bg-gray-50 rounded-lg">
                <p className="text-sm text-gray-600">Preview:</p>
                {createData.prefix ? (
                  <p className="font-mono text-sm">
                    {createData.prefix}{createData.rangeStart.padStart(2, '0')} to {createData.prefix}{createData.rangeEnd.padStart(2, '0')}
                  </p>
                ) : (
                  <p className="font-mono text-sm">
                    {createData.rangeStart} to {createData.rangeEnd}
                  </p>
                )}
                <p className="text-xs text-gray-500 mt-1">
                  Will generate {createData.rangeEnd && createData.rangeStart ? 
                    Math.max(0, parseInt(createData.rangeEnd) - parseInt(createData.rangeStart) + 1) : 0} numbers
                </p>
              </div>
            )}

            <div className="flex justify-end gap-2">
              <Button
                variant="outline"
                onClick={() => setShowCreateDialog(false)}
              >
                Cancel
              </Button>
              <Button
                onClick={handleCreateBatch}
                disabled={loading}
              >
                {loading ? "Creating..." : "Create Batch"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* View OR Batch Dialog */}
      <Dialog open={showViewDialog} onOpenChange={setShowViewDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>
              OR Batch Details - {selectedBatch ? getBranchName(selectedBatch.branch) : ""}
            </DialogTitle>
          </DialogHeader>
          {selectedBatch && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm font-medium">Branch</Label>
                  <p className="text-sm">{getBranchName(selectedBatch.branch)}</p>
                </div>
                <div>
                  <Label className="text-sm font-medium">Prefix</Label>
                  <p className="text-sm font-mono">{selectedBatch.prefix || "None (Direct range)"}</p>
                </div>
                <div>
                  <Label className="text-sm font-medium">Range</Label>
                  <p className="text-sm font-mono">
                    {selectedBatch.rangeStart} - {selectedBatch.rangeEnd}
                  </p>
                </div>
                <div>
                  <Label className="text-sm font-medium">Created</Label>
                  <p className="text-sm">
                    {selectedBatch.createdAt?.toDate()?.toLocaleDateString() || "N/A"}
                  </p>
                </div>
              </div>

              <div className="space-y-2">
                <Label className="text-sm font-medium">Available Numbers</Label>
                <div className="max-h-40 overflow-y-auto p-2 bg-gray-50 rounded border">
                  <div className="grid grid-cols-4 gap-2">
                    {selectedBatch.availableNumbers.map((number) => (
                      <span key={number} className="text-xs font-mono bg-white px-2 py-1 rounded">
                        {number}
                      </span>
                    ))}
                  </div>
                </div>
              </div>

              {selectedBatch.usedNumbers.length > 0 && (
                <div className="space-y-2">
                  <Label className="text-sm font-medium">Used Numbers</Label>
                  <div className="max-h-40 overflow-y-auto p-2 bg-gray-50 rounded border">
                    <div className="grid grid-cols-4 gap-2">
                      {selectedBatch.usedNumbers.map((number) => (
                        <span key={number} className="text-xs font-mono bg-red-100 text-red-700 px-2 py-1 rounded">
                          {number}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Message Dialog */}
      <MessageDialog
        open={message.open}
        onClose={() => setMessage({ ...message, open: false })}
        title={message.type === "error" ? "Error" : message.type === "success" ? "Success" : "Information"}
        message={message.text}
        type={message.type}
      />
    </div>
  );
};

export default ORManagement;
